import React, { useState, useEffect } from "react";
import "../styles/StudentPayments.css";
import {
  getActiveStudentDetails,
  getStudentDetails,
  saveStudentPayment,
} from "../api/StudentDetails";
import { ActiveStudent, Student, StudentPaymentRequest } from "../types";
import CustomDatePicker from "./CustomDatePicker";
import Loader from "../utility/FullPageLoader";
import { LoadingModel } from "../interfaces/LoadingInterface";

interface PaymentFormData {
  studentId: string;
  amount: string;
  fromDate: string;
  toDate: string;
}

interface PaymentFormErrors {
  studentId?: string;
  amount?: string;
  fromDate?: string;
  toDate?: string;
  general?: string;
}

const StudentPayments: React.FC = () => {
  const [formData, setFormData] = useState<PaymentFormData>({
    studentId: "",
    amount: "500",
    fromDate: "",
    toDate: "",
  });

  const [errors, setErrors] = useState<PaymentFormErrors>({});
  const [students, setStudents] = useState<ActiveStudent[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState<LoadingModel>({
    isLoading: false,
    message: "",
  });
  const [isStudentModalOpen, setIsStudentModalOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Fetch registered students
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const response = await getActiveStudentDetails();
        if (response?.data) {
          setStudents(response.data || []);
        }
      } catch (error) {
        console.error("Error fetching students:", error);
        setErrors({ general: "Failed to load students. Please try again." });
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: PaymentFormErrors = {};

    if (!formData.studentId.trim()) {
      newErrors.studentId = "Please select a student";
    }

    if (!formData.amount.trim()) {
      newErrors.amount = "Amount is required";
    } else {
      const amountValue = parseFloat(formData.amount);
      if (isNaN(amountValue) || amountValue <= 0) {
        newErrors.amount = "Please enter a valid amount greater than 0";
      }
    }

    if (!formData.fromDate.trim()) {
      newErrors.fromDate = "From date is required";
    }

    if (!formData.toDate.trim()) {
      newErrors.toDate = "To date is required";
    }

    // Validate date range - to date must be strictly after from date
    if (formData.fromDate && formData.toDate) {
      if (formData.toDate <= formData.fromDate) {
        newErrors.toDate = "To date must be after from date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof PaymentFormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const getMinToDate = (): string | undefined => {
    if (!formData.fromDate) {
      return undefined;
    }

    const [year, month, day] = formData.fromDate.split("-").map(Number);
    const nextDay = new Date(year, month - 1, day + 1);
    const nextYear = nextDay.getFullYear();
    const nextMonth = String(nextDay.getMonth() + 1).padStart(2, "0");
    const nextDayValue = String(nextDay.getDate()).padStart(2, "0");
    return `${nextYear}-${nextMonth}-${nextDayValue}`;
  };

  const handleDateChange = (name: string, date: string) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: date,
      };

      if (
        name === "fromDate" &&
        updated.toDate &&
        updated.toDate <= date
      ) {
        updated.toDate = "";
      }

      return updated;
    });

    if (name === "toDate" && formData.fromDate && date && date <= formData.fromDate) {
      setErrors((prev) => ({
        ...prev,
        toDate: "To date must be after from date",
      }));
      return;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: "",
      ...(name === "fromDate" ? { toDate: "" } : {}),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitLoading({
        isLoading: true,
        message: "Processing payment...",
      });

      const paymentData: StudentPaymentRequest = {
        studentDetailsId: parseInt(formData.studentId),
        amount: parseFloat(formData.amount),
        validityFrom: formData.fromDate,
        validityTo: formData.toDate,
      };
      const response = await saveStudentPayment(paymentData);
      await new Promise((resolve) => setTimeout(resolve, 1500));
      debugger;
      if (response.data != "Payment Successful") {
        throw new Error("Simulated error");
      }
      setIsSubmitted(true);
      setFormData({
        studentId: "",
        amount: "",
        fromDate: "",
        toDate: "",
      });
      setErrors({}); // Clear all errors after successful submission

      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error("Error submitting payment:", error);
      setErrors({
        general: "Failed to process payment. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
      setSubmitLoading({
        isLoading: false,
        message: "",
      });
    }
  };

  const handleReset = () => {
    setFormData({
      studentId: "",
      amount: "500",
      fromDate: "",
      toDate: "",
    });
    setErrors({});
    setIsSubmitted(false);
  };

  const getSelectedStudentName = (): string => {
    if (!formData.studentId) return "";
    const student = students.find(
      (s) => s.studentDetailsId.toString() === formData.studentId
    );
    return student ? `${student.firstName} ${student.lastName}` : "";
  };

  const getSelectedStudent = (): ActiveStudent | undefined => {
    if (!formData.studentId) return undefined;
    return students.find(
      (s) => s.studentDetailsId.toString() === formData.studentId
    );
  };

  const formatJoiningDate = (): string => {
    const student = getSelectedStudent();
    if (!student || !student.dateOfJoining) return "-";
    const date = new Date(student.dateOfJoining);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleStudentSelect = (studentId: string) => {
    setFormData((prev) => ({
      ...prev,
      studentId: studentId,
    }));
    setIsStudentModalOpen(false);
    setSearchTerm("");
    // Clear error when student is selected
    if (errors.studentId) {
      setErrors((prev) => ({
        ...prev,
        studentId: "",
      }));
    }
  };

  const handleOpenStudentModal = () => {
    setIsStudentModalOpen(true);
    setSearchTerm("");
  };

  const handleCloseStudentModal = () => {
    setIsStudentModalOpen(false);
    setSearchTerm("");
  };

  // Filter students based on search term
  const filteredStudents = students.filter((student) => {
    const fullName = `${student.firstName} ${student.lastName}`.toLowerCase();
    const email = student.emailId?.toLowerCase() || "";
    const mobile = student.mobileNumber || "";
    const search = searchTerm.toLowerCase();
    return (
      fullName.includes(search) ||
      email.includes(search) ||
      mobile.includes(search)
    );
  });

  return (
    <div className="student-payments">
      {submitLoading.isLoading && <Loader message={submitLoading.message} />}
      <div className="payments-header">
        <h2>Student Payments</h2>
        <p>Record and manage student payment information</p>
      </div>

      {isSubmitted && (
        <div className="success-banner">
          <div className="success-icon">✓</div>
          <div className="success-content">
            <h4>Payment Recorded Successfully!</h4>
            <p>
              Payment for {getSelectedStudentName()} has been recorded
              successfully.
            </p>
          </div>
        </div>
      )}

      {errors.general && (
        <div className="error-banner">
          <div className="error-icon">✕</div>
          <div className="error-content">
            <h4>Error</h4>
            <p>{errors.general}</p>
          </div>
        </div>
      )}

      <div className="payments-card">
        <div className="payments-form">
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h3>Payment Details</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="studentId">
                    Student <span className="required">*</span>
                  </label>
                  <div className="student-select-container">
                    <button
                      type="button"
                      onClick={handleOpenStudentModal}
                      className={`student-select-btn ${
                        errors.studentId ? "error" : ""
                      }`}
                      disabled={loading || isSubmitting}
                    >
                      {getSelectedStudent() ? (
                        <span className="selected-student">
                          {getSelectedStudent()?.firstName}{" "}
                          {getSelectedStudent()?.lastName} -{" "}
                          {getSelectedStudent()?.mobileNumber}
                        </span>
                      ) : (
                        <span className="placeholder-text">
                          Click to select a student
                        </span>
                      )}
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 9L12 15L18 9"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    {errors.studentId && (
                      <span className="error-message">{errors.studentId}</span>
                    )}
                    {loading && (
                      <span className="field-note">Loading students...</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="amount">
                    Amount (₹) <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    id="amount"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    placeholder="Enter amount"
                    min="0"
                    step="0.01"
                    className={errors.amount ? "error" : ""}
                    disabled={isSubmitting}
                  />
                  {errors.amount && (
                    <span className="error-message">{errors.amount}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dateOfJoining">Date of Joining</label>
                  <input
                    type="text"
                    id="dateOfJoining"
                    name="dateOfJoining"
                    value={formatJoiningDate()}
                    readOnly
                    className="readonly-field"
                    disabled
                  />
                  <span className="field-note">
                    This field is automatically populated
                  </span>
                </div>
                <div className="form-group">
                  {/* Empty div to maintain grid layout */}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fromDate">
                    Validity From Date <span className="required">*</span>
                  </label>
                  <CustomDatePicker
                    value={formData.fromDate}
                    onChange={(date) => handleDateChange("fromDate", date)}
                    error={errors.fromDate}
                    placeholder="Select from date"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="toDate">
                    Validity To Date <span className="required">*</span>
                  </label>
                  <CustomDatePicker
                    value={formData.toDate}
                    onChange={(date) => handleDateChange("toDate", date)}
                    error={errors.toDate}
                    placeholder="Select to date"
                    minDate={getMinToDate()}
                    disabled={!formData.fromDate}
                  />
                  {!formData.fromDate && (
                    <span className="field-note">
                      Select from date first
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleReset}
                disabled={isSubmitting}
              >
                Reset
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting || loading}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Processing...
                  </>
                ) : (
                  "Record Payment"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Student Selection Modal */}
      {isStudentModalOpen && (
        <div className="modal-overlay" onClick={handleCloseStudentModal}>
          <div
            className="student-selection-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Select Student</h2>
              <button
                className="modal-close-btn"
                onClick={handleCloseStudentModal}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className="modal-content">
              <div className="search-container">
                <div className="search-box">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by name, email, or mobile number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>

              <div className="student-list-container">
                {loading ? (
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading students...</p>
                  </div>
                ) : filteredStudents.length === 0 ? (
                  <div className="empty-state">
                    <p>
                      {searchTerm
                        ? "No students found matching your search."
                        : "No students available."}
                    </p>
                  </div>
                ) : (
                  <div className="student-list">
                    {filteredStudents.map((student) => (
                      <div
                        key={student.studentDetailsId}
                        className={`student-item ${
                          formData.studentId ===
                          student.studentDetailsId.toString()
                            ? "selected"
                            : ""
                        }`}
                        onClick={() =>
                          handleStudentSelect(
                            student.studentDetailsId.toString()
                          )
                        }
                      >
                        <div className="student-avatar">
                          {student.firstName.charAt(0).toUpperCase()}
                          {student.lastName.charAt(0).toUpperCase()}
                        </div>
                        <div className="student-info">
                          <div className="student-name">
                            {student.firstName} {student.lastName}
                          </div>
                          <div className="student-details">
                            <span>{student.mobileNumber}</span>
                            {student.emailId && (
                              <>
                                <span className="separator">•</span>
                                <span>{student.emailId}</span>
                              </>
                            )}
                          </div>
                        </div>
                        {formData.studentId ===
                          student.studentDetailsId.toString() && (
                          <div className="selected-indicator">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M20 6L9 17L4 12"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentPayments;
